import React from 'react';

import { Typography } from '@mui/material';
import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import {
  CreditTypeEnum,
  PurchaseCreditSourceType,
  PurchaseCreditType,
} from 'constants/loanApplication';
import {
  creditOptions,
  creditTypeOptionsMap,
  purchaseCreditSourceTypeOptions,
  purchaseCreditSourceTypeOptionsMap,
  purchaseCreditTypeOptions,
  purchaseCreditTypeOptionsMap,
} from 'constants/options';
import { getConditionalObject } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';
import { formatNumberToCurrencyString } from 'utils/number';

import ActionButton from './ActionButton';
import { getAvailableCreditOptions } from './Credits.utils';

export const creditFields = {
  creditType: {
    fieldId: 'creditType',
    type: FieldType.SELECT,
    nameInModel: 'creditType',
    inputProps: {
      options: creditOptions,
      placeholder: 'Select one',
      required: true,
      disablePortal: false,
      withPopupError: true,
    },
  },
  purchaseCreditType: {
    fieldId: 'purchaseCreditType',
    type: FieldType.SELECT,
    nameInModel: 'purchaseCreditType',
    inputProps: {
      options: purchaseCreditTypeOptions,
      placeholder: 'Select one',
      disabled: false,
      required: true,
      disablePortal: false,
      withPopupError: true,
    },
  },
  description: {
    fieldId: 'description',
    type: FieldType.INPUT,
    nameInModel: 'description',
    inputProps: {
      placeholder: 'Please enter',
      required: false,
      type: FieldInputType.TEXT,
      withPopupError: true,
    },
  },
  sourceType: {
    fieldId: 'sourceType',
    type: FieldType.SELECT,
    nameInModel: 'sourceType',
    inputProps: {
      options: purchaseCreditSourceTypeOptions,
      placeholder: 'Select one',
      required: false,
      disablePortal: false,
      withPopupError: true,
    },
  },
  otherDescription: {
    fieldId: 'otherDescription',
    type: FieldType.INPUT,
    nameInModel: 'otherDescription',
    inputProps: {
      placeholder: 'Please enter',
      required: false,
      type: FieldInputType.TEXT,
      withPopupError: true,
    },
  },
  amount: {
    fieldId: 'amount',
    type: FieldType.INPUT,
    nameInModel: 'amount',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Please enter',
      required: true,
      minValue: 0,
      withPopupError: true,
    },
  },
  empty: {
    type: FieldType.EMPTY,
  },
};

export const getCreditConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    isFHARefinance,
    hasUserPermissionToEditLenderCredit,
    creditsList,
    creditType,
    sourceType,
    purchaseCreditType,
    onCreditTypeChange,
    onSourceTypeChange,
    dataId,
  }) => {
    const isOtherCreditType = creditType === CreditTypeEnum.OTHER_CREDIT;
    const isPurchaseCreditType = creditType === CreditTypeEnum.PURCHASE_CREDIT;
    const isEarnestMoneyPurchaseCreditType =
      purchaseCreditType === PurchaseCreditType.EARNEST_MONEY;
    const isOtherSourceType = sourceType === PurchaseCreditSourceType.OTHER;
    const isLenderSourceType = sourceType === PurchaseCreditSourceType.LENDER;
    const isBuilderDeveloperSourceType = sourceType === PurchaseCreditSourceType.BUILDER_DEVELOPER;
    const isOtherSourceTypeCreditExist =
      isOtherSourceType ||
      creditsList.some(
        ({ sourceType, id }) => sourceType === PurchaseCreditSourceType.OTHER && dataId !== id,
      );

    const descriptionField = isPurchaseCreditType
      ? {
          ...creditFields.purchaseCreditType,
          inputProps: {
            ...creditFields.purchaseCreditType.inputProps,
            ...getConditionalObject(
              isPurchaseCreditType && (isLenderSourceType || isBuilderDeveloperSourceType),
              {
                getOptionDisabled: ({ value }) => value === PurchaseCreditType.EARNEST_MONEY,
              },
            ),
          },
        }
      : {
          ...creditFields.description,
          inputProps: {
            ...creditFields.description.inputProps,
            disabled: !isOtherCreditType,
            required: isOtherCreditType,
          },
        };
    const otherDescriptionField = isOtherSourceTypeCreditExist
      ? {
          ...creditFields.otherDescription,
          inputProps: {
            ...creditFields.otherDescription.inputProps,
            disabled: !isOtherSourceType,
            required: isOtherSourceType,
          },
        }
      : creditFields.empty;

    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields: [
            {
              ...creditFields.creditType,
              inputProps: {
                ...creditFields.creditType.inputProps,
                options: getAvailableCreditOptions({
                  creditsList,
                  selectedValue: creditType,
                  hasUserPermissionToEditLenderCredit,
                  isFHARefinance,
                }),
                customOnChange: onCreditTypeChange,
              },
            },
            descriptionField,
            {
              ...creditFields.sourceType,
              inputProps: {
                ...creditFields.sourceType.inputProps,
                disabled: !(isPurchaseCreditType || isOtherCreditType),
                required: isPurchaseCreditType || isOtherCreditType,
                ...getConditionalObject(isPurchaseCreditType && isEarnestMoneyPurchaseCreditType, {
                  getOptionDisabled: ({ value }) =>
                    value === PurchaseCreditSourceType.LENDER ||
                    value === PurchaseCreditSourceType.BUILDER_DEVELOPER,
                }),
                customOnChange: onSourceTypeChange,
              },
            },
            otherDescriptionField,
            creditFields.amount,
          ],
          key: `credit`,
        },
      ],
    };
  },
);

export const getCreditTableConfig = ({
  setEditableTableData,
  deleteCredit,
  editableRowId,
  isActionDisabled,
}) => ({
  creditType: {
    label: 'Credit Type',
    width: '17%',
    sort: true,
    colSpan: ({ creditType }) => creditType?.colSpan,
    customStyles: ({ creditType }) => creditType?.customStyles,
    content: ({ creditType }) =>
      creditType?.content ?? (
        <Typography data-truncated className="truncate" variant="regular14">
          {creditTypeOptionsMap[creditType]?.name || '-'}
        </Typography>
      ),
  },
  description: {
    label: 'Description',
    sort: true,
    width: '18%',
    fieldName: ['description', 'purchaseCreditType'],
    content: ({ description, purchaseCreditType }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {description || purchaseCreditTypeOptionsMap[purchaseCreditType]?.name || '-'}
      </Typography>
    ),
  },
  sourceType: {
    label: 'Source Type',
    sort: true,
    width: '18%',
    content: ({ sourceType }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {sourceType ? purchaseCreditSourceTypeOptionsMap[sourceType].name : '-'}
      </Typography>
    ),
  },
  otherDescription: {
    label: 'Other Description',
    sort: true,
    width: '18%',
    content: ({ otherDescription }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {otherDescription || '-'}
      </Typography>
    ),
  },
  amount: {
    label: 'Amount',
    sort: true,
    width: '17%',
    content: ({ amount }) => (
      <Typography data-truncated className="truncate" variant="regular14">
        {formatNumberToCurrencyString(amount)}
      </Typography>
    ),
    align: 'right',
  },
  action: {
    label: 'Action',
    width: '12%',
    content: rowData => (
      <ActionButton
        deleteTableData={deleteCredit}
        isDisabled={isActionDisabled || (!!editableRowId && rowData.id !== editableRowId)}
        isEditDisabled={rowData.id === editableRowId}
        rowData={rowData}
        setEditableTableData={setEditableTableData}
      />
    ),
    align: 'center',
  },
});
